import { useAuth } from '@shared/hooks';
import { getEnv } from '@shared/utils/env';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

/**
 * AuthGuard component for protecting routes
 * @param children - Components to render if authorized
 * @param requireAuth - If true, requires authentication. If false, requires non-authentication (public routes)
 * @param redirectTo - Custom redirect path
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children, requireAuth = true, redirectTo }) => {
  const { isAuthenticated, forceChangePassword, hasMultipleTenants } = useAuth();
  const location = useLocation();

  // Handle protected routes (requireAuth = true)
  if (requireAuth) {
    if (!isAuthenticated) {
      const defaultTenant = getEnv().VITE_DEFAULT_TENANT;
      return <Navigate to={redirectTo ?? `/auth/login/${defaultTenant}`} replace />;
    }

    // Redirect to password reset if forced (except if already on forgotPassword)
    if (forceChangePassword && !location.pathname.includes('/forgotPassword')) {
      return <Navigate to="/auth/forgotPassword" replace />;
    }

    // Redirect to tenant selection if multiple tenants (except if already on tenants)
    if (hasMultipleTenants && !location.pathname.includes('/tenants')) {
      return <Navigate to="/auth/tenants" replace />;
    }
  }

  // Handle public routes (requireAuth = false)
  if (!requireAuth && isAuthenticated) {
    return <Navigate to={redirectTo ?? '/'} replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
